// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type Concentration,
  type EtfAnalysis,
  type MutualFundAnalysis,
  type PortfolioAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { CorrelationMatrix } from '@advisor-app/components';
import { ConcentrationGraph, TopAssets } from '@advisor-app/features/risk';
import { RiskVsReturnsGraph } from '@shared/ui-kit/components';
import { numberToFixed } from '@shared/ui-kit/utils';
import { type FC, useMemo } from 'react';
import { ValueAtRisk } from './value-at-risk';

import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import { useDestructuredHoldings } from '@advisor-app/hooks/use-destructured-holdings';

export interface RiskTemplateProps {
  analysis: PortfolioAnalysis;
  assetsInfo: AssetsInfoResp[];
}

export const RiskTemplate: FC<RiskTemplateProps> = ({ analysis, assetsInfo }) => {
  const { outstandingBalance: outstandingBalanceValues } =
    useDestructuredHoldings();

  const topHoldingsData = analysis as EtfAnalysis;

  const totalOutstandingBalance = useMemo(
    () => outstandingBalanceValues.reduce((total, value) => total + value, 0),
    [outstandingBalanceValues],
  );

  const companies = useMemo(
    () =>
      topHoldingsData?.top_holdings
        ?.map(holding => {
          const assetData = assetsInfo?.find(
            assetInfo => assetInfo.ticker === holding.ticker,
          ) as Stock;

          const investment = totalOutstandingBalance * holding.concentration;

          /* istanbul ignore next */
          const companyName =
            assetData?.name !== 'missing' ? assetData?.name : holding.name;

          return {
            ticker: holding.ticker,
            weight: holding.concentration,
            name: companyName,
            outstandingBalance: numberToFixed(investment, 0),
            logo: assetData?.url_logo,
            url: assetData?.url_morningstar,
            assetType: assetData?.asset_type,
          };
        })
        .sort((a, b) => b.weight - a.weight),
    [topHoldingsData?.top_holdings, assetsInfo, totalOutstandingBalance],
  );

  const concentrations: Concentration[] = useMemo(
    () =>
      companies.reduce(
        ({ concentrations, value }, { ticker, weight }) => ({
          concentrations: concentrations.concat({
            ticker,
            concentration: value + weight,
          }),
          value: value + weight,
        }),
        { concentrations: [] as Concentration[], value: 0 },
      ).concentrations,
    [companies],
  );

  return (
    <>
      <div className="risk-return-grid" data-testid="risk">
        <ValueAtRisk valueAtRisk={analysis?.var} />
        <RiskVsReturnsGraph risks={analysis?.risk_returns} />
        <ConcentrationGraph concentrations={concentrations} />
        <TopAssets assets={companies} />
      </div>
      <div className="mt-6 grid grid-cols-1">
        <CorrelationMatrix values={(analysis as MutualFundAnalysis)?.corr_matrix} />
      </div>
    </>
  );
};
